import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Flex,
    Grid,
    SimpleGrid,
    Text,
} from "@chakra-ui/react";
import {
    useDispatch,
    useSelector,
} from "react-redux";
import { Wallet,  
    BadgeCheck,
    Clock3,
    TrendingUp,
    ClockArrowUp,
    Users,
    UserPlus
 } from "lucide-react";

import routes from "../../routes";

import Sidebar from "../../components/Sidebar";
import TopBar from "../../components/Topbar";

import PageHeader from "./components/PageHeader";
import StatCard from "./components/StatCard";
import MiniStatCard from "./components/MiniStatCard";
import ReferralCard from "../Profile/components/ReferralCard";
import ProgressCard from "./components/ProgressCard";
import FunnelCard from "./components/FunnelCard";
import Leaderboard from "./components/LeaderBoard";
import RecentReferralsTable from "./components/RecentReferralsTable";
import ReferralGrowthChart from "./components/ReferralGrowthChart";
import CommissionTrendChart from "./components/CommissionTrendChart";
import DashboardSkeleton from "./components/DashboardSkeleton";
import {
    fetchReferredDriversAction,
    fetchReferralMilestonesAction,
    fetchPendingCommissionsAction,
    fetchPartnerPayoutsAction
} from "../../state/actions/driverPartnerActions";
import {
    getNotifications,
} from "../../state/actions/notificationActions";
import RegisterDriverModal from "../RegisterDriver/RegisterDriverModal";
import { getDistrict, findLocation } from "botswana-locations";

const Dashboard = () => {
    const dispatch =
        useDispatch();

    const navigate =
        useNavigate();
    const [
    isRegisterOpen,
    setRegisterOpen,
] = useState(false);
const [showSkeleton, setShowSkeleton] =
    useState(true);
    const {
        driverPartner,
        partnerPayouts = [],
        referredDrivers = [],
        referralMilestones = [],
        pendingCommissions = [],
        loading,
        loaded,
    } = useSelector(
        (state) =>
            state.driverPartner
    );
const [locationSearch, setLocationSearch] = useState("");
const {
    user: authUser,
    isAuthenticated,
} = useSelector(
    state => state.auth
);

useEffect(() => {
    dispatch(fetchReferredDriversAction(1, 10));
    dispatch(fetchReferralMilestonesAction());
    dispatch(fetchPendingCommissionsAction(1, 100));
    dispatch(
        getNotifications(
            null,
            null,
            1,
            10
        )
    );
    if (driverPartner?.driver_partner_id) {
        dispatch(
            fetchPartnerPayoutsAction(
                driverPartner.driver_partner_id,
                1,
                100
            )
        );
    }
}, [
    dispatch,
    driverPartner?.driver_partner_id,
]);
useEffect(() => {
    const timer = setTimeout(() => {
        setShowSkeleton(false);
    }, 3000);

    return () => clearTimeout(timer);
}, []);

useEffect(() => {
    if (!loaded) {
        return;
    }

    if (
        isAuthenticated &&
        authUser &&
        !driverPartner
    ) {
        navigate(
            routes.completeProfile,
            {
                replace: true,
            }
        );
    }
}, [
    loaded,
    isAuthenticated,
    authUser,
    driverPartner,
    navigate,
]);

if (showSkeleton) {
  return (
    <Flex
      h="100vh"
      bg="#F8FAFC"
      overflow="hidden"
    >
      <Box
        w={{
          base: "0",
          md: "280px",
        }}
      >
        <Sidebar />
      </Box>

      <Flex
        flex="1"
        direction="column"
      >
        <TopBar />

        <Box
            p={{
        base: 4,
        md: 8,
    }}
        >
          <DashboardSkeleton />
        </Box>
      </Flex>
    </Flex>
  );
}

    const user =
        driverPartner?.user;

    const fullName =
        [
            user?.first_name,
            user?.middle_name,
            user?.last_name,
        ]
            .filter(
                (v) =>
                    v?.trim()
            )
            .join(" ");

    const displayName =
        fullName ||
        user?.username ||
        "Driver Partner";

    const joinedDate =
        driverPartner?.created_at
            ? new Date(
                  driverPartner.created_at
              ).toLocaleDateString(
                  "en-US",
                  {
                      month:
                          "short",
                      year:
                          "numeric",
                  }
              )
            : "-";

const approvedDrivers =
    referredDrivers.filter(
        driver =>
            driver.driver
                ?.status ===
            "Approved"
    );

const pendingDrivers =
    referredDrivers.filter(
        driver =>
            driver.driver &&
            driver.driver
                ?.status !==
            "Pending"
    );
const paidMilestoneEarnings =
    referralMilestones
        .filter(
            milestone =>
                milestone.commission_status ===
                "Paid"
        )
        .reduce(
            (sum, milestone) =>
                sum +
                Number(
                    milestone.commission_amount ||
                        0
                ),
            0
        );
const paidDeliveryEarnings =
    partnerPayouts
        .filter(
            payout =>
                payout.commission_id &&
                payout.status === "Paid"
        )
        .reduce(
            (sum, payout) =>
                sum + Number(payout.amount || 0),
            0
        );

const totalEarned =
    paidMilestoneEarnings +
    paidDeliveryEarnings;

const pendingMilestoneEarnings =
    referralMilestones
        .filter(
            milestone =>
                milestone.commission_status ===
                "Pending"
        )
        .reduce(
            (sum, milestone) =>
                sum +
                Number(
                    milestone.commission_amount ||
                        0
                ),
            0
        );

const pendingDeliveryEarnings =
    pendingCommissions
        .filter(
            commission =>
                commission.record_type ===
                    "COMMISSION"
        )
        .reduce(
            (sum, commission) =>
                sum +
                Number(
                    commission.commission_amount ||
                        0
                ),
            0
        );

const pendingCommission =
    pendingMilestoneEarnings +
    pendingDeliveryEarnings;

const firstDeliveryCount =
    referralMilestones.filter(
        (
            milestone
        ) =>
            milestone.milestone_type ===
                "First Delivery Completed" &&
            milestone.milestone_status ===
                "Achieved"
    ).length;

const tenDeliveryCount =
    referralMilestones.filter(
        (
            milestone
        ) =>
            milestone.milestone_type ===
                "Ten Deliveries Completed" &&
            milestone.milestone_status ===
                "Achieved"
    ).length;

const thirtyDeliveryCount =
    referralMilestones.filter(
        (
            milestone
        ) =>
            milestone.milestone_type ===
                "Thirty Deliveries Completed" &&
            milestone.milestone_status ===
                "Achieved"
    ).length;

        const monthlyTarget = 1000;

const currentMonth = new Date().getMonth();
const currentYear = new Date().getFullYear();

const paidMilestoneMonthlyEarnings =
    referralMilestones
        .filter(milestone => {
            if (
                milestone.commission_status !==
                "Paid"
            ) {
                return false;
            }

            const date = new Date(
                milestone.updated_at ||
                milestone.created_at
            );

            return (
                date.getMonth() ===
                    currentMonth &&
                date.getFullYear() ===
                    currentYear
            );
        })
        .reduce(
            (sum, milestone) =>
                sum +
                Number(
                    milestone.commission_amount ||
                        0
                ),
            0
        );

const paidDeliveryMonthlyEarnings =
    partnerPayouts
        .filter(payout => {
            if (
                !payout.commission_id ||
                payout.status !== "Paid"
            ) {
                return false;
            }

            const date = new Date(
                payout.paid_at ||
                payout.created_at
            );

            return (
                date.getMonth() === currentMonth &&
                date.getFullYear() === currentYear
            );
        })
        .reduce(
            (sum, payout) =>
                sum + Number(payout.amount || 0),
            0
        );

const monthlyEarnings =
    paidMilestoneMonthlyEarnings +
    paidDeliveryMonthlyEarnings;

const monthlyGoalPercentage =
    Math.min(
        100,
        Math.round(
            (
                monthlyEarnings /
                monthlyTarget
            ) * 100
        )
    );

const canRegisterDriver = ![
    "Rejected",
    "Pending",
    "Suspended",
].includes(
    driverPartner?.status
);
    return (
        <Flex
            h="100vh"
            bg="#F8FAFC"
            overflow="hidden"
            
        >
            <Box
                w={{
                    base: "0",
                    md: "280px",
                }}
                flexShrink={0}
            >
                <Sidebar />
            </Box>

            <Flex
                flex="1"
                direction="column"
                overflow="hidden"
            >
                <TopBar />

                <Box
                    flex="1"
                    overflowY="auto"
                    p={8}
                    marginBottom={12}
                >
                    <PageHeader
                        title={`Welcome back, ${displayName}`}
                        subtitle={`Partner ID: ${
                            driverPartner?.partner_referral_code ||
                            "-"
                        } • Member since ${joinedDate}`}
                        primaryLabel="Register Driver"
                        primaryIcon={UserPlus}
                        onPrimaryClick={() =>
                            setRegisterOpen(
                                true
                            )
                        }
                        primaryDisabled={
                                ![
                                    "Approved",
                                ].includes(
                                    driverPartner?.status
                                )
                            }

                            primaryDisabledTitle="Registration unavailable"

                            primaryDisabledMessage={
                                "You must be an approved Driver Partner to register drivers."
                            }
                                            />
                        {/* <Box
        bg="white"
        borderRadius="16px"
        border="1px solid"
        borderColor="gray.100"
        p={5}
        mb={6}
        boxShadow="sm"
    >
        <Text
            fontSize="sm"
            fontWeight="700"
            color="#2453FF"
            mb={2}
        >
            BOTSWANA LOCATION LOOKUP
        </Text>

        <Text
            fontSize="xl"
            fontWeight="700"
            color="gray"
            mb={1}
        >
            Find a district
        </Text>

        <Text
            fontSize="sm"
            color="gray.500"
            mb={4}
        >
            Search for a Botswana village, town or city.
        </Text>

        <Flex
            direction={{ base: "column", md: "row" }}
            gap={3}
        >
            <Box flex="1">
                <input
                    type="text"
                    value={locationSearch}
                    onChange={(e) =>
                        setLocationSearch(e.target.value)
                    }
                    placeholder="e.g. Gaborone"
                    style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "10px",
                        outline: "none",
                        fontSize: "14px",
                        boxSizing: "border-box"
                    }}
                />
            </Box>

            {locationSearch.trim() && (
                <Box
                    minW={{ md: "250px" }}
                    bg="#F8FAFC"
                    borderRadius="10px"
                    p={3}
                >
                    <Text
                        fontSize="xs"
                        color="gray.500"
                        fontWeight="600"
                    >
                        DISTRICT
                    </Text>

                    <Text
                        fontSize="lg"
                        fontWeight="700"
                        color="#2453FF"
                    >
                        {getDistrict(locationSearch) ||
                            "Location not found"}
                    </Text>
                </Box>
            )}
        </Flex>
    </Box> */}

                    <Grid
                        templateColumns={{
                            base: "1fr",
                            xl: "2fr 320px",
                        }}
                        gap={6}
                    >
                        <Box>
                            <SimpleGrid
                                columns={{
                                    base: 1,
                                    md: 3,
                                }}
                                gap={6}
                            >
                                <StatCard
                                    title="Total Lifetime Earnings"
                                    value={`P${totalEarned.toFixed(
                                        2
                                    )}`}
                                    subtitle={`${approvedDrivers.length} approved referrals`}
                                    icon={
                                        <Wallet
                                            size={
                                                20
                                            }
                                            color="#2453FF"
                                        />
                                    }
                                />
                            
                                <StatCard
                                    title="Pending Commission"
                                    value={`P${pendingCommission.toFixed(
                                        2
                                    )}`}
                                    subtitle="Awaiting payout"
                                    icon={
                                        <ClockArrowUp
                                            size={
                                                20
                                            }
                                            color="#2453FF"
                                        />
                                    }
                                />

                                <StatCard
                                    title="Total Recruits"
                                    value={
                                        referredDrivers.length
                                    }
                                     icon={
                                        <Users
                                            size={
                                                20
                                            }
                                            color="#2453FF"
                                        />
                                    }
                                    subtitle="Drivers recruited"
                                />
                                <StatCard
                                    title="Approved Drivers"
                                    value={
                                        approvedDrivers.length
                                    }
                                    subtitle="Eligible for milestone rewards"
                                    icon={
                                        <BadgeCheck
                                            size={20}
                                            color="#2453FF"
                                        />
                                    }
                                />

                                <StatCard
                                    title="Drivers Awaiting Approval"
                                    value={
                                        pendingDrivers.length
                                    }
                                    subtitle="Still completing onboarding"
                                    icon={
                                        <Clock3
                                            size={20}
                                            color="#2453FF"
                                        />
                                    }
                                />

                                <StatCard
                                    title="Average Earnings Per Driver"
                                    value={`P${
                                        approvedDrivers.length
                                            ? (
                                                totalEarned /
                                                approvedDrivers.length
                                            ).toFixed(2)
                                            : "0.00"
                                    }`}
                                    subtitle="Revenue generated per approved driver"
                                    icon={
                                        <TrendingUp
                                            size={20}
                                            color="#2453FF"
                                        />
                                    }
                                />
                            </SimpleGrid>
                            <Text
                            fontSize="xs"
                            color="gray.400"
                            fontWeight="700"
                            letterSpacing="2px"
                            mt={6}
                            mb={3}
                        >
                            RECRUITMENT MILESTONES ACHIEVED
                        </Text>
                            <SimpleGrid
                                columns={{
                                    base: 2,
                                    lg: 6,
                                }}
                                gap={3}
                                mt={6}
                            >
                                <MiniStatCard
                                    title="Pending"
                                    value={
                                        pendingDrivers.length
                                    }
                                />

                                <MiniStatCard
                                    title="Approved"
                                    value={
                                        approvedDrivers.length
                                    }
                                />

                                <MiniStatCard
                                    title="1st Delivery"
                                    value={
                                        firstDeliveryCount
                                    }
                                />

                                <MiniStatCard
                                    title="10 Deliveries"
                                    value={
                                        tenDeliveryCount
                                    }
                                />

                                <MiniStatCard
                                    title="30 Deliveries"
                                    value={
                                        thirtyDeliveryCount
                                    }
                                />

                               <MiniStatCard
                                    title="Conversion(Pending over Approved)"
                                    value={`${Math.round(
                                        referredDrivers.length
                                            ? (approvedDrivers.length /
                                                referredDrivers.length) *
                                                100
                                            : 0
                                    )}%`}
                                />
                            </SimpleGrid>
                        </Box>

                      <ReferralCard
                        driverPartner={
                            driverPartner
                        }
                        totalRecruits={
                            referredDrivers.length
                        }
                        totalEarned={
                            totalEarned
                        }
                    />

                                        </Grid>

                                        <Grid
                        mt={8}
                        gap={6}
                        templateColumns={{
                            base: "1fr",
                            xl: "2fr 320px",
                        }}
                        alignItems="start"
                    >
                        <ReferralGrowthChart
                            drivers={referredDrivers}
                        />

                        <Flex
                            direction="column"
                            gap={6}
                        >
                            <ProgressCard
                                title="Monthly Goal"
                                percentage={
                                    monthlyGoalPercentage
                                }
                                current={`P${monthlyEarnings.toFixed(
                                    2
                                )}`}
                                target={`P${monthlyTarget.toLocaleString()}`}
                            />

                            <FunnelCard
                                drivers={
                                    referredDrivers
                                }
                                milestones={
                                    referralMilestones
                                }
                            />
                        </Flex>
                    </Grid>

                    <Grid
                        mt={8}
                        gap={6}
                        templateColumns={{
                            base: "1fr",
                            xl: "2fr 370px",
                        }}
                    >
                        <RecentReferralsTable
                            drivers={
                                referredDrivers
                            }
                            milestones={
                                referralMilestones
                            }
                            commissions={pendingCommissions}
                            payouts={partnerPayouts}
                        />

                        <CommissionTrendChart
                            milestones={
                                referralMilestones
                            }
                            commissions={pendingCommissions}
                            payouts={partnerPayouts}
                        />
                    </Grid>
                </Box>
            </Flex>
            <RegisterDriverModal
            open={isRegisterOpen}
            onClose={() =>
                setRegisterOpen(
                    false
                )
            }
        />
        </Flex>
    );
};

export default Dashboard;